import { styled } from "@mui/material";
import { ReactNode } from "react";
import { Property } from "csstype";

const DevErrorCard = styled("section")`
  width: min(1100px, 100%);
  border: 1px solid color-mix(in srgb, var(--foreground) 15%, transparent);
  border-radius: 12px;
  background: color-mix(in srgb, var(--background) 92%, var(--foreground));
  color: var(--foreground);
  box-shadow: 0 20px 35px color-mix(in srgb, black 14%, transparent);
  overflow: hidden;
`;

const DevErrorHeader = styled("header")`
  padding: var(--spacing-4x);
  border-bottom: 1px solid color-mix(in srgb, var(--foreground) 12%, transparent);
  display: flex;
  flex-direction: column;
  gap: var(--spacing);
`;

const DevErrorEyebrow = styled("div")`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: color-mix(in srgb, var(--foreground) 60%, transparent);
`;

const DevErrorTitle = styled("h1")`
  margin: 0;
  font-size: 1.25rem;
  line-height: 1.35;
`;

const DevErrorSubtitle = styled("p")`
  margin: 0;
  font-size: 0.95rem;
  color: color-mix(in srgb, var(--foreground) 72%, transparent);
`;

const DevErrorBody = styled("div")`
  display: grid;
  gap: var(--spacing-3x);
  padding: var(--spacing-4x);
`;

const DevSection = styled("section")`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2x);
`;

const DevSectionTitle = styled("h2")`
  margin: 0;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: color-mix(in srgb, var(--foreground) 65%, transparent);
`;

const DevCodeBlock = styled("pre")`
  margin: 0;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--foreground) 14%, transparent);
  background: color-mix(in srgb, var(--background) 95%, white);
  padding: var(--spacing-2x);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.82rem;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const DevInlinePill = styled("span")`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  background: color-mix(in srgb, var(--foreground) 10%, transparent);
  color: color-mix(in srgb, var(--foreground) 84%, transparent);
`;

export function DeveloperErrorDisplay({ error }: { error: unknown }) {
  const isNativeError = error instanceof Error;
  const errorName = isNativeError ? error.name : "UnknownError";
  const errorMessage = isNativeError
    ? error.message
    : "The thrown value is not an Error instance";
  const errorStack = isNativeError ? error.stack : undefined;
  // TODO: Support `cause` property of chained errors

  return (
    <DevErrorCard>
      <DevErrorHeader>
        <DevErrorEyebrow>Debug Overlay</DevErrorEyebrow>
        <DevErrorTitle>An error occurred while rendering this route</DevErrorTitle>
        <DevErrorSubtitle>
          <DevInlinePill>{errorName}</DevInlinePill>
        </DevErrorSubtitle>
      </DevErrorHeader>

      <DevErrorBody>
        <DevSection>
          <DevSectionTitle>Message</DevSectionTitle>
          <DevCodeBlock>{errorMessage}</DevCodeBlock>
        </DevSection>

        {errorStack ? (
          <DevSection>
            <DevSectionTitle>Stack Trace</DevSectionTitle>
            <ErrorStack stack={errorStack} />
          </DevSection>
        ) : null}

        <DevSection>
          <DevSectionTitle>Raw Error Object</DevSectionTitle>
          <DevCodeBlock>{formatUnknownError(error)}</DevCodeBlock>
        </DevSection>
      </DevErrorBody>
    </DevErrorCard>
  );
}

const StackList = styled("ol")`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--spacing);
`;

const StackItem = styled("li")`
  border: 1px solid color-mix(in srgb, var(--foreground) 11%, transparent);
  background: color-mix(in srgb, var(--background) 96%, white);
  border-radius: 8px;
`;

function ErrorStack({ stack }: { stack: string }): ReactNode {
  const entries = stack
    .split("\n")
    .filter((line) => line.length)
    .map((text) => parseStackLine(text));

  return (
    <StackList>
      {entries.map((entry, index) => (
        <StackItem key={index}>
          <SourceLine entry={entry} />
        </StackItem>
      ))}
    </StackList>
  );
}

const partyColors: Record<SourceParty, Property.Color> = {
  project: "#2e8b57",
  dependency: "#8b5cf6",
  tooling: "#0ea5e9",
};

const SourceAnchor = styled("a")`
  color: inherit;
`;

const SourceLineRoot = styled("div")`
  display: flex;
  align-items: baseline;
  gap: var(--spacing);
  flex-wrap: wrap;
  padding: var(--spacing) var(--spacing-2x);
`;

const SourcePartyPill = styled("span")<{ sourceparty: SourceParty }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: color-mix(
    in srgb,
    ${({ sourceparty }) => partyColors[sourceparty]} 18%,
    transparent
  );
  color: ${({ sourceparty }) => partyColors[sourceparty]};
`;

const SourceFunction = styled("span")`
  font-weight: 700;
  color: color-mix(in srgb, var(--foreground) 94%, transparent);
`;

const SourceMeta = styled("span")`
  color: color-mix(in srgb, var(--foreground) 62%, transparent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
`;

function SourceLine({ entry }: { entry: StackEntry }) {
  const party = entry.party ?? "project";

  return (
    <SourceLineRoot>
      <SourcePartyPill sourceparty={party}>{party}</SourcePartyPill>{" "}
      <SourceFunction>{entry.funcName || "(unnamed)"}</SourceFunction>
      {entry.url ? (
        <SourceAnchor href={entry.url} target="_blank" rel="noreferrer">
          <SourceMeta>{entry.display}</SourceMeta>
        </SourceAnchor>
      ) : (
        <SourceMeta>{entry.display}</SourceMeta>
      )}
    </SourceLineRoot>
  );
}

type SourceParty = "project" | "dependency" | "tooling";

interface StackEntry {
  funcName: string;
  raw: string;
  display: string;
  party?: SourceParty;
  path?: string;
  url?: string;
}

function parseStackLine(text: string): StackEntry {
  // Examples:
  // WarningsInteractiveMapPanel@http://localhost:5173/src/features/dashboard/components/warnings/warnings-interactive-map-panel.tsx?t=1776676708091:83:21
  // @http://localhost:5173/src/features/shared/components/error-boundary/index.tsx?t=1776677250149:173:48
  // async*handleMessage/<@http://localhost:5173/@vite/client:839:55
  // enqueue/<@http://localhost:5173/@vite/client:472:9
  //
  // Shape:
  // {name} @ {link} : {line} : {column}

  const [funcName, rawSource] = text.split("@", 2);
  const raw = rawSource ?? text;

  const url = toURL(raw);
  if (!url) {
    return { funcName, raw, display: raw };
  }
  const pathname = url.pathname;
  if (!pathname || pathname == "/") return { funcName, raw, display: raw };

  const lineColumn = parseLineColumn(raw);
  const display = formatDisplayPath(pathname, lineColumn);

  return {
    funcName,
    raw,
    display,
    path: url.pathname,
    party: parseParty(url.pathname),
    // TODO: The current parsing does not account for source maps, leading to line-number mismatches.
    url: fmtOpenLink(url.pathname, lineColumn?.line),
  };
}

function parseParty(pathname: string): SourceParty {
  if (pathname.startsWith("/node_modules/.vite/")) {
    return "tooling";
  } else if (pathname.startsWith("/node_modules/")) {
    return "dependency";
  } else {
    return "project";
  }
}

function fmtOpenLink(pathname: string, lineNum: string | undefined) {
  const lineSuffix = lineNum && Number.isInteger(Number(lineNum)) ? ":" + lineNum : "";
  return `/__open-in-editor?file=${pathname.slice(1)}${lineSuffix}`;
}

function toURL(url: string): URL | undefined {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
}

function parseLineColumn(raw: string): { line: string; column?: string } | undefined {
  const match = raw.match(/:(\d+)(?::(\d+))?$/);
  if (!match) return undefined;

  return {
    line: match[1],
    column: match[2],
  };
}

function formatDisplayPath(
  pathname: string,
  lineColumn: { line: string; column?: string } | undefined,
) {
  if (!lineColumn) return pathname;
  return lineColumn.column
    ? `${pathname}:${lineColumn.line}:${lineColumn.column}`
    : `${pathname}:${lineColumn.line}`;
}

function formatUnknownError(error: unknown): string {
  if (error instanceof Error) {
    return JSON.stringify(
      {
        ...error,
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      null,
      2,
    );
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}
